import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { usePatients, useCreatePatient } from "@/hooks/usePatients";
import { usePatientEncounters, useCreatePatientEncounter } from "@/hooks/usePatientEncounters";
import { getPatientEncounterLabel } from "./patientEncounterLabel";

interface PatientEncounterSelectorProps {
  customerId: string;
  selectedPatientId?: string;
  selectedEncounterId?: string;
  onPatientChange: (patientId: string) => void;
  onEncounterChange: (encounterId: string) => void;
}

export function PatientEncounterSelector({
  customerId,
  selectedPatientId,
  selectedEncounterId,
  onPatientChange,
  onEncounterChange,
}: PatientEncounterSelectorProps) {
  const [newPatientId, setNewPatientId] = useState("");
  const [newEncounterDateTime, setNewEncounterDateTime] = useState("");
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [showNewEncounterForm, setShowNewEncounterForm] = useState(false);

  const { data: patients, isFetching: isFetchingPatients } = usePatients(customerId);
  const { data: encounters, isFetching: isFetchingEncounters } = usePatientEncounters(selectedPatientId);
  const createPatient = useCreatePatient();
  const createEncounter = useCreatePatientEncounter();

  const handleCreatePatient = async () => {
    if (!newPatientId.trim()) return;

    try {
      const patient = await createPatient.mutateAsync({
        id: newPatientId.trim(),
        customer_id: customerId,
      });
      onPatientChange(patient.id);
      setNewPatientId("");
      setShowNewPatientForm(false);
    } catch (error) {
      console.error("Error creating patient:", error);
    }
  };

  const handleCreateEncounter = async () => {
    if (!newEncounterDateTime.trim() || !selectedPatientId) return;

    try {
      const encounter = await createEncounter.mutateAsync({
        patient_id: selectedPatientId,
        customer_id: customerId,
        observation_datetime: new Date(newEncounterDateTime).toISOString(),
        patient_age: 0, // Default age, should be provided by user in production
        patient_sex: "U" as const, // Default to Unknown
        specialty: "endoscopy" as const,
      });
      onEncounterChange(encounter.id);
      setNewEncounterDateTime("");
      setShowNewEncounterForm(false);
    } catch (error) {
      console.error("Error creating encounter:", error);
    }
  };

  if (isFetchingPatients || isFetchingEncounters) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Patient</Label>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-16" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Encounter</Label>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-16" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Patient</Label>
        <div className="flex gap-2">
          <Select
            name="patient-select"
            value={selectedPatientId || ""}
            onValueChange={onPatientChange}
            disabled={!customerId}
          >
            <SelectTrigger aria-label="Patient" className="flex-1">
              <SelectValue placeholder="Select a patient" />
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[200px]">
                {patients?.map((patient) => (
                  <SelectItem key={patient.id} value={patient.id}>
                    {patient.id}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowNewPatientForm(!showNewPatientForm)}
            disabled={!customerId}
          >
            New
          </Button>
        </div>

        {showNewPatientForm && (
          <div className="flex gap-2 mt-2">
            <Input placeholder="Patient ID" value={newPatientId} onChange={(e) => setNewPatientId(e.target.value)} />
            <Button
              type="button"
              onClick={handleCreatePatient}
              disabled={!newPatientId.trim() || createPatient.isPending}
            >
              Create
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNewPatientForm(false);
                setNewPatientId("");
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Encounter</Label>
        <div className="flex gap-2">
          <Select value={selectedEncounterId || ""} onValueChange={onEncounterChange} disabled={!selectedPatientId}>
            <SelectTrigger aria-label="Encounter" className="flex-1">
              <SelectValue placeholder="Select an encounter" />
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[200px]">
                {encounters?.map((encounter) => (
                  <SelectItem key={encounter.id} value={encounter.id}>
                    {getPatientEncounterLabel(encounter)}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowNewEncounterForm(!showNewEncounterForm)}
            disabled={!selectedPatientId}
          >
            New
          </Button>
        </div>

        {showNewEncounterForm && (
          <div className="flex gap-2 mt-2">
            <Input
              type="datetime-local"
              placeholder="Observation date and time"
              value={newEncounterDateTime}
              onChange={(e) => setNewEncounterDateTime(e.target.value)}
            />
            <Button
              type="button"
              onClick={handleCreateEncounter}
              disabled={!newEncounterDateTime.trim() || createEncounter.isPending}
            >
              Create
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNewEncounterForm(false);
                setNewEncounterDateTime("");
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
